import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Shield, Lock, Eye, EyeOff, AlertCircle, CheckCircle, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { extractError } from "../lib/errors";

// Replica de la validación del backend: 10-128 chars, 3 de 4 clases
function isStrongPassword(pw) {
  if (!pw || pw.length < 10 || pw.length > 128) return false;
  let classes = 0;
  if (/[a-z]/.test(pw)) classes++;
  if (/[A-Z]/.test(pw)) classes++;
  if (/\d/.test(pw)) classes++;
  if (/[^A-Za-z0-9]/.test(pw)) classes++;
  return classes >= 3;
}

const ResetPasswordPage = () => {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") || "";

  const [verifying, setVerifying] = useState(true);
  const [tokenState, setTokenState] = useState(null); // null | {valid, email_hint}
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  // B12 (S16b-2): show success screen with 2FA lockout notice instead of immediate redirect
  const [resetDone, setResetDone] = useState(false);

  // Verifica el token al montar
  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenState({ valid: false });
      return;
    }

    const verify = async () => {
      try {
        const res = await axios.get(`${API}/auth/reset-password/verify`, {
          params: { token },
        });
        setTokenState(res.data);
      } catch {
        setTokenState({ valid: false });
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [token]);

  const handleReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error(t("auth.reset.passwords_no_match"));
      return;
    }

    if (!isStrongPassword(newPassword)) {
      toast.error(
        locale === "es"
          ? "La contraseña debe tener 10-128 caracteres y usar 3 de: minúsculas, mayúsculas, números, símbolos."
          : "Password must be 10-128 chars and use 3 of: lowercase, uppercase, digits, symbols."
      );
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      // B12 (S16b-2): show success screen with 2FA notice instead of navigating immediately.
      // Per ADR-S16b-002, 2FA is NOT disabled on reset — user may need admin intervention.
      setResetDone(true);
    } catch (err) {
      toast.error(extractError(err, locale === "es" ? "Error al restablecer contraseña" : "Failed to reset password"));
    } finally {
      setLoading(false);
    }
  };

  // Cargando verificación
  if (verifying) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Token inválido o vencido
  if (!tokenState?.valid) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6">
        <Card className="w-full max-w-md border-border shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 rounded-xl bg-destructive/10 mx-auto flex items-center justify-center mb-4">
              <AlertCircle className="w-7 h-7 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-heading">
              {t("auth.reset.invalid_token")}
            </CardTitle>
            <CardDescription>
              {t("auth.reset.invalid_token_desc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/auth/forgot">
              <Button className="w-full" data-testid="request-new-link">
                {t("auth.reset.request_new")}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // B12 (S16b-2): success screen with persistent 2FA lockout notice (ADR-S16b-002)
  if (resetDone) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6">
        <div className="w-full max-w-md space-y-4">
          <Card className="border-border shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="w-12 h-12 rounded-xl bg-primary mx-auto flex items-center justify-center mb-4">
                <CheckCircle className="w-7 h-7 text-primary-foreground" />
              </div>
              <CardTitle className="text-2xl font-heading">
                {t("auth.reset.success_heading")}
              </CardTitle>
              <CardDescription>
                {t("auth.reset.success_desc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Persistent 2FA lockout notice — never auto-dismiss (ADR-S16b-002) */}
              <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-300">
                    {t("auth.reset.2fa_banner.title")}
                  </p>
                  <p className="text-xs text-amber-200/80">
                    {t("auth.reset.2fa_banner.body")}
                  </p>
                </div>
              </div>
              <Button
                className="w-full"
                onClick={() => navigate("/auth")}
                data-testid="go-to-signin"
              >
                {t("auth.reset.go_to_signin")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Formulario de nueva contraseña
  return (
    <div className="dark min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6">
      <div className="w-full max-w-md">
        <Card className="border-border shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 rounded-xl bg-primary mx-auto flex items-center justify-center mb-4">
              <Shield className="w-7 h-7 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl font-heading">
              {t("auth.reset.title")}
            </CardTitle>
            {tokenState.email_hint && (
              <CardDescription>
                {locale === "es" ? `Para: ${tokenState.email_hint}` : `For: ${tokenState.email_hint}`}
              </CardDescription>
            )}
          </CardHeader>

          <CardContent>
            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">{t("auth.reset.new_password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type={showPw ? "text" : "password"}
                    placeholder="••••••••••"
                    className="pl-10 pr-10"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={10}
                    maxLength={128}
                    autoComplete="new-password"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none rounded"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {locale === "es"
                    ? "Mínimo 10 caracteres; usar 3 de: minúsculas, mayúsculas, números, símbolos."
                    : "Min 10 chars; use 3 of: lowercase, uppercase, digits, symbols."}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t("auth.reset.confirm_password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type={showConfirm ? "text" : "password"}
                    placeholder="••••••••••"
                    className="pl-10 pr-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={10}
                    maxLength={128}
                    autoComplete="new-password"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none rounded"
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="reset-submit"
              >
                {loading
                  ? locale === "es" ? "Guardando..." : "Saving..."
                  : t("auth.reset.submit")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
